import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { BoxRequest, ListInclude as BoxRequestListInclude, ListFilters as BoxRequestListFilters, Statuses as BoxRequestStatuses, StatusKey as BoxRequestStatusKey } from '@/models/BoxRequest';
import DataTable, { TableOptions, TableVisibility } from '@/components/data-table/DataTable';
import RequestSearchInput from '@/components/request/RequestSearchInput/RequestSearchInput.vue';
import RequestStatusFilter from '@/components/request/RequestStatusFilter/RequestStatusFilter.vue';
import RequestStylistFilter from '@/components/request/RequestStylistFilter/RequestStylistFilter.vue';
import RequestTypeFilter from '@/components/request/RequestTypeFilter/RequestTypeFilter.vue';
import RequestEditDialog from '@/components/request/RequestEditDialog/RequestEditDialog.vue';
import { User } from '@/models/User';
import StyledBox, { StyledBoxOptions } from '@/support/StyledBoxType';
import { DateTime } from 'luxon';

@Component<RequestDataTable>({
  components: {
    'data-table': DataTable,
    'request-search-input': RequestSearchInput,
    'request-status-filter': RequestStatusFilter,
    'request-stylist-filter': RequestStylistFilter,
    'request-type-filter': RequestTypeFilter,
    'request-edit-dialog': RequestEditDialog,
  }
})
export default class RequestDataTable extends Vue {
  @Prop()
  private user!: User;

  private requestSearchInput: string = '';

  private requestStatusFilter: string = '';

  private requestStylistFilter: string = '';

  private requestTypeFilter: string = '';

  private editingBoxRequest: BoxRequest | null = null;

  protected isShowingFilters = false;

  protected filtersHeight = 0;

  protected isLoadingFilters = false;

  protected showFilters(): void {
    if (!this.isShowingFilters) {
      this.isShowingFilters = true;
      const innerFilters = document.getElementsByClassName('inner-filters');
      this.filtersHeight = innerFilters[0].clientHeight;
    } else {
      this.isShowingFilters = false;
      this.filtersHeight = 0;
    }
  }

  public refresh(): void {
    if (!this.dataTable) { return; }
    this.dataTable.refresh();
  }

  private get dataTable(): DataTable | void {
    if (!this.$refs.dataTable) { return; }
    return this.$refs.dataTable as DataTable;
  }

  private get includes(): BoxRequestListInclude[] {
    return ['orders', 'stylist', 'user'];
  }

  private setEditingBoxRequest(request: BoxRequest) {
    this.editingBoxRequest = request;
  }

  private get filters(): BoxRequestListFilters {
    return {
      ...this.requestSearchInput ? { search: this.requestSearchInput } : {},
      ...this.requestStatusFilter ? { status: this.requestStatusFilter } : {},
      ...this.requestStylistFilter ? { stylist: this.requestStylistFilter } : {},
      ...this.requestTypeFilter ? { type: this.requestTypeFilter } : {},
      ...this.user && this.user.id ? { user: this.user.id } : {},
    };
  }

  private get tableOptions(): TableOptions {
    return {
      model: new BoxRequest()
        .include(this.includes)
        .filter(this.filters)
        .limit(9000),
      headers: [
        {
          text: '',
          value: 'id',
          width: '3%',
          transform: (user: User, request: BoxRequest): string => {
            return `<i aria-hidden="true" class="v-icon material-icons theme--light view" style="font-size: 18px;">remove_red_eye</i>`
          },
          action: '/aanvragen/{id}',
        },
        {
          text: '',
          value: 'id',
          width: '3%',
          transform: (user: User, request: BoxRequest): string => {
            return `<i aria-hidden="true" class="v-icon material-icons theme--light view" style="font-size: 18px;">edit</i>`
          },
          action: (request: BoxRequest): void => this.setEditingBoxRequest(request),
        },
        {
          text: this.$tc('tableHeaders.client.title'),
          value: 'user',
          width: '10%',
          transform: (user: User): string => user && user.display_name ? user.display_name : '',
          action: '/contacten/{user.id}',
          sortable: {
            key: "user",
            order: "ASC"
          },
        },
        {
          text: this.$tc('tableHeaders.status.title'),
          value: 'status',
          width: '7%',
          transform: (statusKey: BoxRequestStatusKey): string => this.statusses[statusKey],
        },
        {
          text: this.$tc('tableHeaders.type.title'),
          value: 'type',
          width: '7%',
          transform: (typeKey: string) => {
            const option = this.requestTypeFilterOptions.find(option => option.value === typeKey);
            return option ? option.text : '';
          },
        },
        {
          text: this.$tc('tableHeaders.stylist.title'),
          value: 'stylist',
          width: '10%',
          transform: (client: User): string => client && client.name ? client.name : '',
          sortable: {
            key: "stylist",
            order: "ASC"
          },
        },
        {
          text: this.$tc('tableHeaders.note.title'),
          value: 'note',
          width: '15%',
        },
        {
          text: this.$tc('tableHeaders.boxNumber.title'),
          value: 'order_count',
          width: '6%',
        },
        {
          text: this.$tc('tableHeaders.channel.title'),
          value: 'channel',
          width: '7%',
        },
        {
          text: this.$tc('tableHeaders.createdAt.title'),
          value: 'created_at',
          width: '15%',
          transform: (date: string): string => {
            if (!date || !DateTime.fromSQL(date).isValid) {
              return '-'
            }
            return DateTime.fromSQL(date).toFormat('dd-LL-yyyy');
          },
          sortable: {
            key: "created_at",
            order: "ASC"
          },
        },
        {
          text: this.$tc('tableHeaders.assignedAt.title'),
          value: 'assigned_at',
          width: '15%',
          transform: (date: string): string => {
            if (!date || !DateTime.fromSQL(date).isValid) {
              return '-'
            }
            return DateTime.fromSQL(date).toFormat('dd-LL-yyyy');
          },
          sortable: {
            key: "assigned_at",
            order: "ASC"
          },
        },
        {
          text: this.$tc('tableHeaders.lastStatusUpdate.title'),
          value: 'status_updated_at',
          width: '15%',
          transform: (date: string): string => {
            if (!date || !DateTime.fromSQL(date).isValid) {
              return '-'
            }
            return DateTime.fromSQL(date).toFormat('dd-LL-yyyy');
          },
        },
      ],
      actions: [],
      filters: [],
    };
  }

  private get isPlanner() {
    if (!this.$store || !this.$store.state || !this.$store.state.Auth) {
      return false;
    }

    // @ts-ignore
    return this.$store.state.Auth.isPlanner()
  }

  private get tableVisibility(): TableVisibility {
    return {
      checkboxes: false,
      title: false,
      total: true,
      search: true,
      limit: true,
      pagination: true,
    };
  }

  private get statusses(): BoxRequestStatuses {
    return BoxRequest.statuses;
  }

  protected get requestTypeFilterOptions(): StyledBoxOptions[] {
    return StyledBox.styledBoxOptions.filter(option =>
      ['swap_box', 'styled_box', 'after_sales'].includes(option.value as string)
    );
  }

};
